import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Settings2 } from "lucide-react";
import { fmtRM, COMPONENT_CATEGORIES } from "@/pages/hr/payroll/payrollUtils";

export default function PayrollComponentsTab({ employees = [], components = [], reloadComponents }) {
  const { can } = useAuth();
  const canStatutory = can("payroll:statutory_manage");
  const canAssign = can("payroll:manage_profiles");
  const [section, setSection] = useState("catalog");
  const [compEdit, setCompEdit] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [filterEmp, setFilterEmp] = useState("");
  const [asgForm, setAsgForm] = useState(null);

  const loadAssignments = () => {
    const q = filterEmp ? `?employee_id=${filterEmp}` : "";
    api.get(`/hr/payroll/assignments${q}`).then((r) => setAssignments(r.data)).catch(() => {});
  };
  useEffect(() => { if (section === "assignments") loadAssignments(); /* eslint-disable-next-line */ }, [section, filterEmp]);

  const delComp = async (c) => { if (!window.confirm(`Delete component ${c.code}?`)) return;
    try { await api.delete(`/hr/payroll/components/${c.id}`); toast.success("Deleted"); reloadComponents(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delAsg = async (id) => { try { await api.delete(`/hr/payroll/assignments/${id}`); toast.success("Removed"); loadAssignments(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div data-testid="payroll-components-tab">
      <div className="flex gap-2 mb-3">
        <button data-testid="components-section-catalog" onClick={() => setSection("catalog")} className={`px-3 py-1.5 text-sm rounded-md ${section === "catalog" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>Component Catalog</button>
        <button data-testid="components-section-assignments" onClick={() => setSection("assignments")} className={`px-3 py-1.5 text-sm rounded-md ${section === "assignments" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>Employee Assignments</button>
      </div>

      {section === "catalog" && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <p className="text-xs text-slate-500">Configurable earnings / deductions / statutory components. Business allowances are never hard-coded in calculation.</p>
            {canStatutory && <Button size="sm" className="bg-slate-900" data-testid="component-add-btn" onClick={() => setCompEdit({ category: "earning", taxable: true, epf_applicable: true, socso_applicable: true, eis_applicable: true, active: true })}><Plus className="w-4 h-4 mr-1" />Add Component</Button>}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="excel-table w-full" data-testid="components-table">
              <thead><tr><th>Code</th><th>Name</th><th>Category</th><th>Taxable</th><th>EPF</th><th>SOCSO</th><th>EIS</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {components.map((c) => (
                  <tr key={c.id} data-testid={`component-row-${c.code}`}>
                    <td className="font-mono text-xs">{c.code}{c.system && <Badge variant="outline" className="ml-1 text-[10px]">system</Badge>}</td>
                    <td>{c.name}</td><td><Badge variant="outline">{c.category}</Badge></td>
                    <td className="text-center">{c.taxable ? "✓" : "—"}</td><td className="text-center">{c.epf_applicable ? "✓" : "—"}</td>
                    <td className="text-center">{c.socso_applicable ? "✓" : "—"}</td><td className="text-center">{c.eis_applicable ? "✓" : "—"}</td>
                    <td className="text-center">{c.active ? "✓" : "—"}</td>
                    <td className="text-right">{canStatutory && <div className="flex gap-1 justify-end">
                      <button onClick={() => setCompEdit(c)} data-testid={`component-edit-${c.code}`}><Settings2 className="w-4 h-4 text-slate-500" /></button>
                      {!c.system && <button onClick={() => delComp(c)} data-testid={`component-del-${c.code}`}><Trash2 className="w-4 h-4 text-rose-500" /></button>}
                    </div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {section === "assignments" && (
        <div>
          <div className="flex justify-between items-end mb-2">
            <div><Label className="text-xs">Filter by employee</Label>
              <Select value={filterEmp || "all"} onValueChange={(v) => setFilterEmp(v === "all" ? "" : v)}>
                <SelectTrigger className="h-8 w-56" data-testid="assign-filter-emp"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All employees</SelectItem>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} · {e.full_name}</SelectItem>)}</SelectContent>
              </Select></div>
            {canAssign && <Button size="sm" className="bg-slate-900" data-testid="assign-add-btn" onClick={() => setAsgForm({ employee_id: "", component_id: "", amount: "", recurring: true, effective_from: "", effective_to: "", active: true })}><Plus className="w-4 h-4 mr-1" />Add Assignment</Button>}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="excel-table w-full" data-testid="assignments-table">
              <thead><tr><th>Employee</th><th>Component</th><th>Amount</th><th>Type</th><th>Period / Effective</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {assignments.map((a) => (
                  <tr key={a.id} data-testid={`assignment-row-${a.id}`}>
                    <td className="font-mono text-xs">{a.employee_id}</td>
                    <td>{a.component_name} <span className="text-xs text-slate-400">({a.component_code})</span></td>
                    <td>{fmtRM(a.amount)}</td>
                    <td>{a.recurring ? <Badge variant="outline">Recurring</Badge> : <Badge variant="outline" className="text-indigo-700 border-indigo-300">One-off</Badge>}</td>
                    <td className="text-xs">{a.recurring ? `${a.effective_from || "—"} → ${a.effective_to || "open"}` : `${a.period_year}-${String(a.period_month).padStart(2, "0")}`}</td>
                    <td className="text-center">{a.active ? "✓" : "—"}</td>
                    <td className="text-right">{canAssign && <button onClick={() => delAsg(a.id)} data-testid={`assignment-del-${a.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></button>}</td>
                  </tr>
                ))}
                {assignments.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-6">No assignments.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {compEdit && <ComponentDialog comp={compEdit} onClose={() => setCompEdit(null)} onSaved={reloadComponents} />}
      {asgForm && <AssignmentDialog form={asgForm} setForm={setAsgForm} employees={employees} components={components} onClose={() => setAsgForm(null)} onSaved={loadAssignments} />}
    </div>
  );
}

function ComponentDialog({ comp, onClose, onSaved }) {
  const [c, setC] = useState(comp);
  const [busy, setBusy] = useState(false);
  const isNew = !comp.id;
  const set = (k, v) => setC({ ...c, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      if (isNew) await api.post("/hr/payroll/components", { ...c, order: c.order || 99 });
      else await api.put(`/hr/payroll/components/${c.id}`, c);
      toast.success("Saved"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="component-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add" : "Edit"} Component</DialogTitle><DialogDescription>Define an earning, deduction or statutory component.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {isNew && <div><Label className="text-xs">Code</Label><Input value={c.code || ""} onChange={(e) => set("code", e.target.value)} data-testid="component-code-input" placeholder="e.g. TRAVEL_ALLOW" /></div>}
          <div><Label className="text-xs">Name</Label><Input value={c.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="component-name-input" /></div>
          <div><Label className="text-xs">Category</Label>
            <Select value={c.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="component-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{COMPONENT_CATEGORIES.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
            </Select></div>
          {c.category === "earning" && (
            <div className="grid grid-cols-2 gap-2">
              {["taxable", "epf_applicable", "socso_applicable", "eis_applicable"].map((k) => (
                <div key={k} className="flex items-center gap-2"><Switch checked={!!c[k]} onCheckedChange={(v) => set(k, v)} data-testid={`component-${k}`} /><Label className="text-xs">{k.replace("_applicable", "").replace("_", " ")}</Label></div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2"><Switch checked={!!c.active} onCheckedChange={(v) => set("active", v)} /><Label className="text-xs">Active</Label></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-slate-900" disabled={busy} onClick={save} data-testid="component-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignmentDialog({ form, setForm, employees, components, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const earnDed = components.filter((c) => c.category !== "statutory" && c.active);
  const save = async () => {
    if (!form.employee_id || !form.component_id || form.amount === "") return toast.error("Employee, component and amount required");
    setBusy(true);
    try {
      await api.post("/hr/payroll/assignments", {
        employee_id: form.employee_id, component_id: form.component_id, amount: Number(form.amount),
        recurring: form.recurring, effective_from: form.effective_from || null, effective_to: form.effective_to || null,
        period_year: form.recurring ? null : Number(form.period_year), period_month: form.recurring ? null : Number(form.period_month),
        active: form.active, note: form.note || "",
      });
      toast.success("Assignment added"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="assignment-dialog">
        <DialogHeader><DialogTitle>Add Component Assignment</DialogTitle><DialogDescription>Effective-dated recurring or one-off component for an employee (incl. Attendance Allowance base).</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Employee</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
              <SelectTrigger data-testid="assign-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} · {e.full_name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Component</Label>
            <Select value={form.component_id} onValueChange={(v) => setForm({ ...form, component_id: v })}>
              <SelectTrigger data-testid="assign-component"><SelectValue placeholder="Select component" /></SelectTrigger>
              <SelectContent>{earnDed.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.category})</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Monthly Amount (RM)</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="assign-amount" /></div>
          <div className="flex items-center gap-2"><Switch checked={!!form.recurring} onCheckedChange={(v) => setForm({ ...form, recurring: v })} data-testid="assign-recurring" /><Label className="text-xs">Recurring (vs one-off period)</Label></div>
          {form.recurring ? (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Effective From</Label><Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="assign-eff-from" /></div>
              <div><Label className="text-xs">Effective To</Label><Input type="date" value={form.effective_to} onChange={(e) => setForm({ ...form, effective_to: e.target.value })} /></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Period Year</Label><Input type="number" value={form.period_year || ""} onChange={(e) => setForm({ ...form, period_year: e.target.value })} data-testid="assign-period-year" /></div>
              <div><Label className="text-xs">Period Month</Label><Input type="number" min="1" max="12" value={form.period_month || ""} onChange={(e) => setForm({ ...form, period_month: e.target.value })} data-testid="assign-period-month" /></div>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-slate-900" disabled={busy} onClick={save} data-testid="assign-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
